import React, { useMemo } from 'react';
import { View, TouchableOpacity, StyleSheet } from 'react-native';
import { Text } from '@/components/common/text';
import { useMarketplaceColors } from '@/constants/residents/marketplaceColors';
import { useThemeColors } from '@/hooks/shared/useThemeColors';
import { onColor } from '@/constants/shared/theme/onColor';
import { FollowingUser } from '@/types/residents/marketplaceListing';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { UserAvatar } from './shared';

const ChevronRightIcon = RESIDENTS_SVG_COMPONENTS.CHEVRON_RIGHT;
const CheckIcon = RESIDENTS_SVG_COMPONENTS.CHECK;

interface FollowingUserCardProps {
  user: FollowingUser;
  onFollowToggle: (userId: string) => void;
  onProfilePress: (userId: string) => void;
}

export const FollowingUserCard: React.FC<FollowingUserCardProps> = ({
  user,
  onFollowToggle,
  onProfilePress,
}) => {
  const mc = useMarketplaceColors();
  const { semantic } = useThemeColors();

  // `accentBlue` resolves to the light brand blue in BOTH themes and reads
  // at ~3.0:1 on the light card — below the AA floor. `text.link` is the
  // per-theme accent: unchanged in dark, darkened brand blue in light.
  const linkColor = semantic.text.link;

  const styles = useMemo(() => StyleSheet.create({
    container: {
      backgroundColor: mc.cardBackground,
      borderRadius: 16,
      padding: 16,
      gap: 8,
    },
    topRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
    },
    followButton: {
      backgroundColor: mc.accentBlueSolid,
      borderRadius: 8,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
      paddingHorizontal: 16,
      paddingVertical: 8,
    },
    followingButton: {
      backgroundColor: 'transparent',
      borderWidth: 1,
      borderColor: mc.accentBlueSolid,
    },
    followButtonText: {
      fontSize: 12,
      fontWeight: '500',
      // Fixed brand-blue fill in both themes; a themed ink flips to
      // near-black in light and drops to 3.8:1. Derive the ink from the fill.
      color: onColor(mc.accentBlueSolid),
      lineHeight: 18,
    },
    followingButtonText: {
      color: linkColor,
    },
    bottomRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-end',
      gap: 20,
    },
    userName: {
      flex: 1,
      fontSize: 14,
      fontWeight: '600',
      color: mc.textPrimary,
      lineHeight: 21,
    },
    navigationContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 2,
    },
    newListingDot: {
      width: 8,
      height: 8,
      borderRadius: 4,
      backgroundColor: mc.productivityOrange,
    },
  }), [mc, linkColor]);

  const handleFollowPress = () => {
    onFollowToggle(user.id);
  };

  const handleCardPress = () => {
    onProfilePress(user.id);
  };

  return (
    <View style={styles.container}>
      {/* Top Row - Avatar and Follow Button */}
      <View style={styles.topRow}>
        <UserAvatar
          initials={user.initials}
          avatarUrl={user.avatarUrl}
          size="large"
        />
        
        <TouchableOpacity
          style={[
            styles.followButton,
            user.isFollowing && styles.followingButton,
          ]}
          onPress={handleFollowPress}
          activeOpacity={0.8}
        >
          {user.isFollowing && (
            <CheckIcon width={18} height={18} color={linkColor} />
          )}
          <Text style={[
            styles.followButtonText,
            user.isFollowing && styles.followingButtonText,
          ]}>
            {user.isFollowing ? 'Following' : 'Follow'}
          </Text>
        </TouchableOpacity>
      </View>

      {/* Bottom Row - Name and Navigation */}
      <TouchableOpacity 
        style={styles.bottomRow}
        onPress={handleCardPress}
        activeOpacity={0.8}
      >
        <Text style={styles.userName} numberOfLines={1}>
          {user.name}
        </Text>
        <View style={styles.navigationContainer}>
          {user.hasNewListing && (
            <View style={styles.newListingDot} />
          )}
          <ChevronRightIcon width={20} height={20} color={mc.textMuted} />
        </View>
      </TouchableOpacity>
    </View>
  );
};

